import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  AlertCircle,
  ArrowLeft,
  ArrowUpCircle,
  BadgeCheck,
  BadgeDollarSign,
  Box,
  CheckCircle,
  CircleDot,
  Clock,
  Inbox,
  MapPin,
  Package,
  Truck,
  User,
  XCircle,
} from "lucide-react";
import { shipmentService, trackingEventService } from "@/lib/database";
import { supabase, type Shipment, type TrackingEvent } from "@/lib/supabase";
import { toast } from "sonner";
import CostBreakdown from "@/components/admin/shipment/CostBreakdown";

export default function ViewShipment() {
  const { trackingNumber } = useParams();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trackingEvents, setTrackingEvents] = useState<TrackingEvent[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const navigate = useNavigate();

  useEffect(() => {
    loadShipment();
  }, []);

  useEffect(() => {
    loadTrackingEvents();
  }, [shipment]);

  const getTrackingSteps = (shipment: Shipment) => {
    if (!shipment) return [];

    const isUsToHt = shipment.route === "us_to_ht";
    const origin = isUsToHt ? "US" : "Haiti";
    const destination = isUsToHt ? "Haiti" : "US";

    const steps: {
      id: string;
      label: string;
      completed: boolean;
      current?: boolean;
    }[] = [];
    const statusOrder: string[] = [];

    steps.push(
      { id: "created", label: "Shipment created", completed: false },
      {
        id: "estimated",
        label: "Cost and charges estimated",
        completed: false,
      },
      { id: "confirmed", label: "Shipment confirmed", completed: false },
    );

    statusOrder.push("created", "estimated", "confirmed");

    if (shipment.is_pickup_requested) {
      steps.push({
        id: "picking_up_from_sender",
        label: `Pickup scheduled from sender's address in ${
          destination === "US" ? "Haiti" : "The US"
        }`,
        completed: false,
      });
      statusOrder.push("picking_up_from_sender");
    }

    steps.push(
      {
        id: "received_at_warehouse",
        label: `Package received at our ${origin} warehouse`,
        completed: false,
      },
      {
        id: "processing_at_warehouse",
        label: `Processing at our ${origin} warehouse for international transit`,
        completed: false,
      },
      {
        id: "in_transit_to_country",
        label: `In transit to ${destination === "US" ? "the US" : "Haiti"}`,
        completed: false,
      },
      {
        id: "arrived_at_warehouse",
        label: `Arrived at our ${destination} facility`,
        completed: false,
      },
    );

    statusOrder.push(
      "received_at_warehouse",
      "processing_at_warehouse",
      "in_transit_to_country",
      "arrived_at_warehouse",
    );

    if (shipment.is_delivery_requested) {
      steps.push(
        {
          id: "in_transit_to_recipient",
          label: `Out for delivery in ${
            destination === "US" ? "the US" : "Haiti"
          }`,
          completed: false,
        },
        {
          id: "delivered_to_recipient",
          label: `Delivered to recipient in ${
            destination === "US" ? "the US" : "Haiti"
          }`,
          completed: false,
        },
      );
      statusOrder.push("in_transit_to_recipient", "delivered_to_recipient");
    } else {
      steps.push(
        {
          id: "ready_for_pickup",
          label: `Ready for pickup in ${
            destination === "US" ? "the US" : "Haiti"
          }`,
          completed: false,
        },
        {
          id: "picked_up_by_recipient",
          label: `Picked up by recipient in ${
            destination === "US" ? "the US" : "Haiti"
          }`,
          completed: false,
        },
      );
      statusOrder.push("ready_for_pickup", "picked_up_by_recipient");
    }

    const currentIndex = statusOrder.indexOf(shipment.status!);

    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      current: index === currentIndex,
    }));
  };

  const loadTrackingEvents = async () => {
    if (!shipment) return;
    try {
      const events = await trackingEventService.getByShipmentId(
        shipment.id,
        true,
      );
      setTrackingEvents(events);
    } catch (error) {
      console.error("Error loading tracking events:", error);
    }
  };

  // const handleEdit = () => {
  //   navigate(`/admin/shipments/${trackingNumber}/edit`);
  // }

  const handleStatusChange = async (
    newStatus: string,
    direction: "forward" | "backward",
  ) => {
    if (!shipment) return;

    try {
      setSaving(true);
      const updatedShipment = await shipmentService.updateStatus(
        shipment.id,
        newStatus,
        "Admin Dashboard",
        `Status changed from "${shipment.status!.replace(
          new RegExp("_", "g"),
          " ",
        )}" to "${newStatus.replace(new RegExp("_", "g"), " ")}"`,
        localStorage.getItem("userId") || undefined,
      );

      // If moving forward in the process, send email notification
      if (direction === "forward") {
        // Send email notification
        // TODO: Move to shipment service
        try {
          const { error } = await supabase.functions.invoke("send-email", {
            method: "POST",
            body: JSON.stringify({
              data: {
                shipment: updatedShipment,
                customer: updatedShipment.customer,
              },
              type: "new_shipment_status",
            }),
          });

          toast.success("Shipment status updated & email sent", {
            position: "top-center",
            closeButton: true,
          });

          if (error) {
            throw new Error(error.message || "Failed to send shipment email");
          }
        } catch (emailError: any) {
          console.error("Email sending error:", emailError);
          toast.error(
            "Shipment updated, but failed to send email notification.",
            {
              position: "top-center",
              closeButton: true,
            },
          );
          return;
        }
      }

      setShipment(updatedShipment);
      loadTrackingEvents(); // Reload tracking events to show the new status
    } catch (error) {
      console.error("Error updating shipment status:", error);
      alert("Error updating shipment status. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleNextStep = async () => {
    if (!shipment) return;

    const steps = getTrackingSteps(shipment);

    if (currentStep < steps.length - 1) {
      const nextStep = steps[currentStep + 1];

      await handleStatusChange(nextStep.id, "forward");
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = async () => {
    if (!shipment) return;

    const steps = getTrackingSteps(shipment);
    if (currentStep > 0) {
      const prevStep = steps[currentStep - 1];

      await handleStatusChange(prevStep.id, "backward");
      setCurrentStep(currentStep - 1);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "picked_up_by_recipient":
      case "delivered_to_recipient":
        return <CheckCircle className="h-4 w-4 text-green-500" />;

      case "in_transit_to_country":
      case "in_transit_to_recipient":
        return <Truck className="h-4 w-4 text-blue-500" />;

      case "ready_for_pickup":
        return <Package className="h-4 w-4 text-purple-500" />;

      case "arrived_at_warehouse":
        return <AlertCircle className="h-4 w-4 text-orange-500" />;

      case "processing_at_warehouse":
        return <Clock className="h-4 w-4 text-yellow-500" />;

      case "picking_up_from_sender":
        return <ArrowUpCircle className="h-4 w-4 text-teal-500" />;

      case "received_at_warehouse":
        return <Inbox className="h-4 w-4 text-sky-500" />;

      case "created":
        return <CircleDot className="h-4 w-4 text-gray-500" />;

      case "estimated":
        return <BadgeDollarSign className="h-4 w-4 text-amber-500" />;

      case "confirmed":
        return <BadgeCheck className="h-4 w-4 text-green-600" />;

      case "cancelled":
      case "refunded":
        return <XCircle className="h-4 w-4 text-red-500" />;

      default:
        return <XCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      picked_up_by_recipient:
        "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
      delivered_to_recipient:
        "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
      in_transit_to_country:
        "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
      in_transit_to_recipient:
        "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
      ready_for_pickup:
        "bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200",
      arrived_at_warehouse:
        "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200",
      processing_at_warehouse:
        "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200",
      picking_up_from_sender:
        "bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-200",
      received_at_warehouse:
        "bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-200",
      created: "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200",
      estimated:
        "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200",
      confirmed:
        "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
      cancelled: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
      refunded: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
    };

    return (
      <Badge
        className={
          variants[status as keyof typeof variants] ||
          "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200"
        }
      >
        {status.replace(new RegExp("_", "g"), " ").toUpperCase()}
      </Badge>
    );
  };

  const loadShipment = async () => {
    try {
      setLoading(true);
      const data = await shipmentService.getByTrackingNumber(trackingNumber!);

      // Set current step based on shipment status
      if (data) {
        const steps = getTrackingSteps(data);
        const currentStepIndex = steps.findIndex((step) => step.current);
        setCurrentStep(currentStepIndex >= 0 ? currentStepIndex : 0);
      }

      setShipment(data);
    } catch (error) {
      console.error("Error loading shipment:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!shipment) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading shipment details...</div>
      </div>
    );
  }

  const packageItems =
    shipment.packages?.flatMap((pkg) => pkg.items || []) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Shipment Details
            </h1>
            <p className="text-gray-600">
              Tracking Number: {shipment.tracking_number}
            </p>
          </div>
        </div>

        {/* <Button onClick={handleEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button> */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Shipment Information</CardTitle>
              <CardDescription>
                Basic shipment details and status
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tracking Number</Label>
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-gray-400" />
                    <span className="font-mono">
                      {shipment.tracking_number}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>

                  <div className="flex items-center space-x-2">
                    {getStatusIcon(shipment.status!)}
                    {getStatusBadge(shipment.status!)}
                  </div>
                </div>

                <div className="space-y-2 flex flex-col">
                  <Label>Transit Route</Label>

                  <span>
                    {shipment.route === "us_to_ht"
                      ? "USA to Haiti"
                      : "Haiti to USA"}
                  </span>
                </div>

                <div className="space-y-2 flex flex-col">
                  <Label>Shipping Method</Label>
                  <Badge variant="outline" className="capitalize w-fit">
                    {shipment.shipping_method}
                  </Badge>
                </div>

                {shipment.shipping_method === "air" && (
                  <div className="space-y-2 flex flex-col">
                    <Label>Total Weight</Label>
                    <span>{shipment.total_weight} lbs</span>
                  </div>
                )}

                {shipment.is_pickup_requested && (
                  <div className="space-y-2 flex flex-col">
                    <Label>Pickup Address</Label>
                    <span className="text-sm text-gray-600">
                      {shipment.pickup_address}
                    </span>
                  </div>
                )}

                {shipment.is_delivery_requested && (
                  <div className="space-y-2 flex flex-col">
                    <Label>Delivery Address</Label>
                    <span className="text-sm text-gray-600">
                      {shipment.delivery_address}
                    </span>
                  </div>
                )}
              </div>

              {/* {shipment.dimensions && (
                  <div className="space-y-2">
                    <Label>Dimensions</Label>
                    <span>{shipment.dimensions} inches</span>
                  </div>
                )} */}

              {/* <div className="space-y-2">
                  <Label>Package Contents</Label>
                  <p className="text-sm">{shipment.package_contents}</p>
                </div> */}

              {shipment.special_instructions && (
                <div className="space-y-2">
                  <Label>Special Instructions</Label>
                  <p className="text-sm text-gray-600">
                    {shipment.special_instructions}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
              <CardDescription>Details about the customer</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2 flex flex-col">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span>
                    {shipment.customer?.first_name}{" "}
                    {shipment.customer?.last_name}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    {shipment.customer?.email}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Information */}
          <Card>
            <CardHeader>
              <CardTitle>Pricing Information</CardTitle>
              <CardDescription>Cost breakdown and fees</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="pt-2 border-t gap-2">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">Total Cost</Label>

                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-bold text-green-600">
                      ${shipment.total_cost}
                    </span>
                  </div>
                </div>

                <CostBreakdown
                  shipment={shipment}
                  shipmentDiscounts={shipment.codes || []}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Shipment Items */}
          {packageItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Box className="h-4 w-4 mr-2" />
                  Package Items ({packageItems.length})
                </CardTitle>
                <CardDescription>
                  Individual items in this shipment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {packageItems.map((item) => (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.item_name}
                          </p>
                          {item.item_description && (
                            <p className="text-sm text-gray-600">
                              {item.item_description}
                            </p>
                          )}
                          {item.category && (
                            <p className="text-xs text-gray-500">
                              Category: {item.category!.name}
                            </p>
                          )}
                        </div>
                        <div className="text-right text-sm text-gray-600">
                          <p>Qty: {item.quantity}</p>
                          {item.total_weight && (
                            <p>Weight: {item.total_weight} lbs</p>
                          )}
                          {/* {item.value && <p>Value: ${item.value}</p>} */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tracking Progress */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery Progress</CardTitle>
              <CardDescription>
                Current shipment status and tracking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousStep}
                  disabled={currentStep === 0 || saving}
                >
                  Previous Step
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextStep}
                  disabled={
                    currentStep === getTrackingSteps(shipment).length - 1 ||
                    saving
                  }
                >
                  Next Step
                </Button>
              </div>
              <div className="space-y-3">
                {getTrackingSteps(shipment).map((step, index) => (
                  <div key={step.id} className="flex items-center space-x-3">
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        step.completed
                          ? "bg-[#254164] text-white"
                          : step.current
                            ? "bg-blue-100 text-blue-600 border-2 border-blue-600"
                            : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {step.completed ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <span className="text-xs font-medium">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-sm font-medium ${
                          step.completed || step.current
                            ? "text-gray-900"
                            : "text-gray-500"
                        }`}
                      >
                        {step.label}
                      </p>
                      {step.current && (
                        <p className="text-xs text-blue-600">Current status</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tracking Events */}
          {trackingEvents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Tracking History</CardTitle>
                <CardDescription>Detailed tracking events</CardDescription>
              </CardHeader>
              <CardContent className="h-auto max-h-60 overflow-y-auto">
                <div className="space-y-3">
                  {trackingEvents.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg"
                    >
                      <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900 capitalize">
                            {event.status.replace(new RegExp("_", "g"), " ")}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(event.event_timestamp!).toLocaleString()}
                          </p>
                        </div>
                        {event.location && (
                          <p className="text-sm text-gray-600 flex items-center mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            {event.location}
                          </p>
                        )}
                        {event.description && (
                          <p className="text-sm text-gray-600 mt-1">
                            {event.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle>Important Dates</CardTitle>
              <CardDescription>Key shipment dates and timeline</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Created:</span>
                  <span>
                    {new Date(shipment.created_at!).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Last Updated:</span>
                  <span>
                    {new Date(shipment.updated_at!).toLocaleDateString()}
                  </span>
                </div>
                {shipment.estimated_delivery_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Estimated Delivery:</span>
                    <span>
                      {new Date(
                        shipment.estimated_delivery_date,
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {shipment.actual_delivery_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Actual Delivery:</span>
                    <span>
                      {new Date(
                        shipment.actual_delivery_date,
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
